import * as React from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * ErrorBoundary — the crash-proof law for route shells: ANY failure while
 * rendering or lazy-loading a view must surface this error card with a
 * retry, never silently unmount the app. Without it, a rejected dynamic
 * import (deploy swapped hashed assets under an open tab) or any render
 * error inside a lazy route unmounts the whole React tree — the blank
 * production-only page class (CORE-168).
 *
 *   <ErrorBoundary title="This view failed to load" resetKey={pathname}>
 *     <Outlet />
 *   </ErrorBoundary>
 *
 * Retry modes: "reload" (default) reloads the app — the only retry that
 * recovers a FAILED DYNAMIC IMPORT, because a rejected React.lazy module
 * stays rejected for the session; a fresh load also picks up redeployed
 * asset hashes. "reset" re-renders the subtree in place for transient
 * render failures without a reload.
 */
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Heading inside the fallback card. */
  title?: string;
  /** One-line-100 explanation under the heading. */
  description?: string;
  /**
   * When this value changes (e.g. the route pathname), a caught error is
   * cleared so the next navigation retries the subtree automatically.
   */
  resetKey?: string;
  /** How "Try again" recovers (see doc comment). Default "reload". */
  retryMode?: "reset" | "reload";
}

interface ErrorBoundaryState {
  error: Error | null;
  /** Mirrors the last-seen resetKey for the adjust-in-render reset. */
  prevResetKey: string | undefined;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, prevResetKey: undefined };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    // Adjust-in-render: a navigation (new resetKey) clears a caught error
    // so moving to another route is itself the retry.
    if (state.prevResetKey !== props.resetKey) {
      return { prevResetKey: props.resetKey, error: null };
    }
    return null;
  }

  componentDidCatch(error: Error): void {
    // Keep the failure visible in the console (stack only; content-free).
    console.error("Uncaught render/lazy-load failure:", error);
  }

  retry = (): void => {
    if ((this.props.retryMode ?? "reload") === "reload") {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (this.state.error !== null) {
      return (
        <Card role="alert" className="m-4 max-w-3xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle
                aria-hidden="true"
                className="size-4 text-status-blocked"
              />
              {this.props.title ?? "Something went wrong"}
            </CardTitle>
            <CardDescription>
              {this.props.description ??
                "This view failed to load. Try again — your data is safe."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={this.retry}>
              Try again
            </Button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}
